import { useReactiveVar } from '@apollo/client';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import ViewComfyIcon from '@mui/icons-material/ViewComfy';
import ViewComfyAltIcon from '@mui/icons-material/ViewComfyAlt';
import ViewListIcon from '@mui/icons-material/ViewList';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from '@tanstack/react-router';
import type React from 'react';
import { useEffect } from 'react';
import { GALLERY_ONLY, GRID, INLINE, TWOUP, VIEW_CONTEXT } from 'src/constants';
import { getPersistedLayout, isValidLayout, persistLayout } from 'src/helpers/layoutPersistence';
import { akomiMediaCardGridVar } from 'src/store/akomiMediaCardGridVar';
import type { LayoutType } from 'src/types/store.types';
import type { BaseSearchParams } from 'src/utils/searchSchemas';

type ViewContextType = (typeof VIEW_CONTEXT)[keyof typeof VIEW_CONTEXT];

interface LayoutIconsProps {
  context?: ViewContextType;
}

const LayoutIcons: React.FC<LayoutIconsProps> = ({ context = VIEW_CONTEXT.LIBRARY }) => {
  const { layout } = useReactiveVar(akomiMediaCardGridVar);
  const navigate = useNavigate();

  // Re-hydrate layout from localStorage when context changes (e.g. library <-> gallery).
  // Each context has its own valid layouts, so we re-read the persisted value rather than
  // running once on mount — TanStack Router may reuse this component across route changes.
  useEffect(() => {
    const initialLayout = getPersistedLayout(context) as LayoutType;
    akomiMediaCardGridVar({
      ...akomiMediaCardGridVar(),
      layout: initialLayout,
    });
  }, [context]);

  const setLayout = (nextLayout: LayoutType): void => {
    if (!isValidLayout(context, nextLayout)) {
      nextLayout = GRID as LayoutType;
    }
    const prevLayout = akomiMediaCardGridVar().layout;

    // Build the search params update based on layout selection
    const searchUpdates: Partial<BaseSearchParams> = { layout: nextLayout };

    if (context === VIEW_CONTEXT.LIBRARY) {
      if (nextLayout === GALLERY_ONLY) {
        searchUpdates.scope = 'galleries';
        searchUpdates.page = 1;
      } else if (prevLayout === GALLERY_ONLY) {
        searchUpdates.scope = undefined;
        searchUpdates.page = 1;
      }
    }

    // Update URL via TanStack Router navigate
    navigate({
      search: ((prev: Record<string, unknown>) => ({
        ...prev,
        ...searchUpdates,
        // biome-ignore lint/suspicious/noExplicitAny: TanStack Router navigate requires type cast for dynamic search params
      })) as any,
    });

    akomiMediaCardGridVar({
      ...akomiMediaCardGridVar(),
      layout: nextLayout,
    });
    persistLayout(context, nextLayout);
  };

  return (
    <div style={{ display: 'flex' }}>
      {context !== VIEW_CONTEXT.GALLERY && (
        <Tooltip title="Gallery Only View" arrow enterDelay={50} placement="bottom">
          <IconButton
            onClick={() => setLayout(GALLERY_ONLY as LayoutType)}
            size="large"
            aria-label="Gallery only view"
            data-testid="layout-gallery-only"
          >
            <PhotoLibraryIcon style={{ color: layout === GALLERY_ONLY ? 'inherit' : 'gray' }} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Grid View" arrow enterDelay={50} placement="bottom">
        <IconButton
          onClick={() => setLayout(GRID as LayoutType)}
          size="large"
          aria-label="Grid view"
          data-testid="layout-grid"
        >
          <ViewComfyIcon
            style={{
              color: layout === GRID || !layout ? 'inherit' : 'gray',
            }}
            id="cyp-grid-view-icon"
          />
        </IconButton>
      </Tooltip>
      <Tooltip title="List View" arrow enterDelay={50} placement="bottom">
        <IconButton
          onClick={() => setLayout(INLINE as LayoutType)}
          size="large"
          aria-label="List view"
          data-testid="layout-list"
        >
          <ViewListIcon id="cyp-list-view-icon" style={{ color: layout === INLINE ? 'inherit' : 'gray' }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Twoup View" arrow enterDelay={50} placement="bottom">
        <IconButton
          onClick={() => setLayout(TWOUP as LayoutType)}
          size="large"
          aria-label="Two-up view"
          data-testid="layout-two-up"
        >
          <ViewComfyAltIcon id="cyp-two-up-view-icon" style={{ color: layout === TWOUP ? 'inherit' : 'gray' }} />
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default LayoutIcons;
